/**
 * Service Layer Entry Point
 * Provides a unified interface for billing API services
 */

import {
  CostCategoriesService as RealCostCategoriesService,
  RealBusinessHoursService,
  RealBillingAppConfigService,
  RealRateMasterService,
  RealBillingRecordsService,
  RealBillingCalculationService,
  RealShopWorxService,
} from './realApi';

/**
 * Unified API Service Interface
 */
class UnifiedApiService {
  constructor() {
    this.switchToRealApi();
  }

  configureFromAppConfig() {
    this.switchToRealApi();
  }

  /**
   * Gets the current API mode
   * @returns {string} Current API mode
   */
  getApiMode() {
    return this.isRealApiEnabled() ? 'real' : 'unconfigured';
  }

  /**
   * Checks if real API is enabled
   * @returns {boolean} True if real API is enabled
   */
  isRealApiEnabled() {
    return this.rateMasterService === RealRateMasterService
      && this.costCategoriesService === RealCostCategoriesService;
  }

  /**
   * Switches to real API mode (runtime switch)
   * @returns {void}
   */
  switchToRealApi() {
    this.rateMasterService = RealRateMasterService;
    this.billingRecordsService = RealBillingRecordsService;
    this.shopWorxService = RealShopWorxService;
    this.billingCalculationService = RealBillingCalculationService;
    this.costCategoriesService = RealCostCategoriesService;
    this.businessHoursService = RealBusinessHoursService;
    this.billingAppConfigService = RealBillingAppConfigService;
  }

  /**
   * Tests API connectivity
   * @returns {Promise<Object>} Connection test results
   */
  async testConnectivity() {
    const results = {
      mode: this.getApiMode(),
      shopWorx: false,
      database: false,
      timestamp: new Date().toISOString(),
    };

    try {
      // Test ShopWorx connection
      results.shopWorx = await this.shopWorxService.testConnection();
    } catch (error) {
      console.error('ShopWorx connectivity test failed:', error);
      results.shopWorxError = error.message;
    }

    try {
      // Test database connection by fetching rates
      const rates = await this.rateMasterService.getAllRates();
      results.database = Array.isArray(rates);
    } catch (error) {
      console.error('Database connectivity test failed:', error);
      results.databaseError = error.message;
    }

    return results;
  }
}

// Create singleton instance
const apiService = new UnifiedApiService();

const serviceProxy = (getService) => new Proxy({}, {
  get(_, property) {
    const service = getService();
    return service ? service[property] : undefined;
  },
});

// Export individual services for direct access
export const RateMasterService = serviceProxy(() => apiService.rateMasterService);
export const BillingRecordsService = serviceProxy(() => apiService.billingRecordsService);
export const ShopWorxService = serviceProxy(() => apiService.shopWorxService);
export const BillingCalculationService = serviceProxy(() => apiService.billingCalculationService);
export const CostCategoriesService = serviceProxy(() => apiService.costCategoriesService);
export const BusinessHoursService = serviceProxy(() => apiService.businessHoursService);
export const BillingAppConfigService = serviceProxy(() => apiService.billingAppConfigService);

export const refreshApiServices = () => {
  apiService.configureFromAppConfig();
};

// Export unified service
export const ApiService = apiService;

// Export service classes for advanced usage
export {
  RealRateMasterService,
  RealBillingRecordsService,
  RealShopWorxService,
  RealBillingCalculationService,
  RealCostCategoriesService,
  RealBusinessHoursService,
  RealBillingAppConfigService,
};

// Default export
export default {
  ApiService,
  RateMasterService,
  BillingRecordsService,
  ShopWorxService,
  BillingCalculationService,
  CostCategoriesService,
  BusinessHoursService,
  BillingAppConfigService,

  // Utility functions
  switchToRealApi: () => apiService.switchToRealApi(),
  testConnectivity: () => apiService.testConnectivity(),
  getApiMode: () => apiService.getApiMode(),
  isRealApiEnabled: () => apiService.isRealApiEnabled(),
};
